/* eslint-disable camelcase */
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'
import { main, mobileMediaQuery } from '@caterdesk/ui--theme'
import { Text, TextExtraExtraSmall, TextExtraSmall } from '@caterdesk/ui--typography'
import Box from '@caterdesk/ui--box'
import Stack from '@caterdesk/ui--stack'
import { MenuVendor } from '../../../domain/menu-vendor'
import { MenuItem } from '../../../domain/menu-item'
import { SectionTitle } from './index.styles'
import { useMedia } from 'react-use'
import { PieChart, useDrawingArea } from '@mui/x-charts'
import { DialogContent, styled as muiStyled } from '@mui/material'
import { TFunction } from 'i18next'

type Props = {
  vendor: MenuVendor
  item: MenuItem
}

const Serves = styled.span`
  color: ${main.colors.text};
`

const Macro = styled(Box)`
  width: 100%;
  border-radius: 5px;
  padding: 8px 16px;
  background: ${main.colors.background_light};
`

const StyledText = muiStyled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 20,
}))

function PieCenterLabel({ amount, t }: { amount: number; t: TFunction }) {
  const { width, height, left, top } = useDrawingArea()
  const centerX = left + width / 2
  const centerY = top + height / 2
  const lineSpacing = 25

  return (
    <StyledText x={centerX} y={centerY}>
      <tspan x={centerX} y={centerY - lineSpacing / 2}>
        {amount}
      </tspan>
      <tspan x={centerX} y={centerY + lineSpacing / 2}>
        {t('marketplace:calories')}
      </tspan>
    </StyledText>
  )
}

export const NutritionalTab: React.FC<Props> = ({ vendor, item }) => {
  const isMobileScreen = useMedia(mobileMediaQuery, false)
  const { t } = useTranslation()

  const {
    macros_kcal,
    macros_protein,
    macros_carbs,
    macros_fat,
    macros_sugar,
    macros_saturates,
    macros_fibre,
    macros_salt,
  } = item

  const hasNutritionalData = Boolean(
    macros_kcal ||
      macros_protein ||
      macros_carbs ||
      macros_fat ||
      macros_sugar ||
      macros_saturates ||
      macros_fibre ||
      macros_salt,
  )

  const macros = [
    {
      label: 'kcal',
      value: macros_kcal,
    },
    {
      label: 'protein',
      value: macros_protein,
    },
    {
      label: 'carbs',
      value: macros_carbs,
    },
    {
      label: 'fat',
      value: macros_fat,
    },
    {
      label: 'sugar',
      value: macros_sugar,
    },
    {
      label: 'sat. fat',
      value: macros_saturates,
    },
    {
      label: 'fibre',
      value: macros_fibre,
    },
    {
      label: 'salt',
      value: macros_salt,
    },
  ]

  return (
    <DialogContent>
      <Stack direction="vertical" spacing={16} alignItems="stretch">
        {match(hasNutritionalData)
          .with(true, () => (
            <>
              <Box>
                <SectionTitle color={main.colors.text_heading}>
                  {t('marketplace:nutritionalInformation')}
                </SectionTitle>
                <Box padding={{ y: 4 }}>
                  <TextExtraSmall style={{ lineHeight: '24px' }}>
                    {t('cc:allInformationProvidedBy')} {vendor.companyName}
                  </TextExtraSmall>
                  <TextExtraSmall style={{ lineHeight: '24px' }}>
                    {t('marketplace:dailyCalorieNote')}
                  </TextExtraSmall>
                </Box>
              </Box>

              {match(item)
                .with(
                  {
                    macros_protein: P.number,
                    macros_carbs: P.number,
                    macros_fat: P.number,
                    macros_kcal: P.number,
                  },
                  ({ macros_protein, macros_carbs, macros_fat, macros_kcal }) => {
                    const pieChartData = [
                      {
                        value: macros_protein,
                        label: 'Protein',
                      },
                      {
                        value: macros_carbs,
                        label: 'Carbohydrates',
                      },
                      {
                        value: macros_fat,
                        label: 'Fat',
                      },
                    ]
                    return (
                      <Box>
                        <PieChart
                          series={[
                            {
                              data: pieChartData,
                              valueFormatter: (item: { value: number }) => `${item.value}g`,
                              innerRadius: 70,
                            },
                          ]}
                          height={200}
                        >
                          <PieCenterLabel amount={macros_kcal} t={t} />
                        </PieChart>
                      </Box>
                    )
                  },
                )
                .otherwise(() => null)}

              <SectionTitle color={main.colors.text_heading}>
                {t('marketplace:baseDish')}{' '}
                <Serves style={{ textTransform: 'capitalize' }}>
                  ({t('marketplace:serves', { servesAmount: 1 }).toLowerCase()})
                </Serves>
              </SectionTitle>
              <Stack spacing={4} growChildren={isMobileScreen ? 'all' : undefined}>
                {macros.map((macro) =>
                  macro.value ? (
                    <Macro key={macro.label} padding={{ y: 8 }} centerContent>
                      <Stack direction="vertical" spacing={4}>
                        <Text style={{ color: main.colors.text_heading }}>
                          {match(macro)
                            .with({ label: 'kcal' }, (macro) => macro.value)
                            .with({ label: 'salt' }, (macro) => `${macro.value || ''}mg`)
                            .otherwise(() => `${macro.value || ''}g`)}
                        </Text>
                        <TextExtraExtraSmall>{macro.label}</TextExtraExtraSmall>
                      </Stack>
                    </Macro>
                  ) : null,
                )}
              </Stack>
            </>
          ))
          .with(false, () => (
            <Box>
              <SectionTitle color={main.colors.text_heading}>
                {t('marketplace:noNutritionalInfo')}
              </SectionTitle>
              <TextExtraSmall style={{ lineHeight: '24px' }}>
                {t('marketplace:waitingOnInfoFrom')} {vendor.companyName}
              </TextExtraSmall>
            </Box>
          ))
          .exhaustive()}
      </Stack>
    </DialogContent>
  )
}
